/* -----------------Globals--------------- */
import { useEffect, useState } from "react";

/* -----------------Types--------------- */
import type { StyleProp, ViewStyle } from "react-native";
import type { FeedbackParams } from "../feedback";
import type { HandleTracking } from "../types";

/* -----------------Child components--------------- */
import FullReportDialog from "./FullReportDialog";
import SessionAnalysisCard from "./SessionAnalysisCard";

/* -----------------Helpers & Hooks--------------- */
import { useSessionAnalysis } from "./hooks/useSessionAnalysis";

/* -----------------Constants--------------- */
import { events } from "./constants";

//@ts-ignore
import packageJson from "../../package.json";
import type { SessionMetadata } from "./types";

type Props = {
	sessionId: number;
	handleTracking: HandleTracking;
	feedbackDialogParams?: FeedbackParams;
	context: string;
	bottomContent?: React.ReactNode;
	shouldShowFullReport?: boolean;
	sessionMetadata?: SessionMetadata;
	appFont?: string;
	outputLanguage?: string;
	fontSize?: number;
	demoExamId: string;
	examId?: string | number;
	appId?: string;
	getAuthHeaders?: () => Promise<Record<string, string>>;
	userData?: Record<string, unknown>;
	goldStandardData?: Record<string, unknown>;
	cardTitle?: string;
	cardSubtitle?: string;
	cardIcon?: string;
	cardContentHeight?: number;
	cardActionLabel?: string;
	cardContainerStyle?: StyleProp<ViewStyle>;
	cardMode?: "elevated" | "outlined" | "contained";
	ready?: boolean;
	additionalContext?: string;
};
export const SessionAnalysis = ({
	sessionId,
	handleTracking,
	feedbackDialogParams,
	context,
	bottomContent,
	shouldShowFullReport,
	sessionMetadata,
	appFont,
	outputLanguage,
	fontSize,
	demoExamId,
	examId,
	appId,
	getAuthHeaders,
	userData,
	goldStandardData,
	cardTitle,
	cardSubtitle,
	cardIcon,
	cardContentHeight,
	cardActionLabel,
	cardContainerStyle,
	cardMode,
	ready,
	additionalContext,
}: Props) => {
	const examName = context;
	const tenantName = feedbackDialogParams?.companyName ?? "";
	const { sessionAnalysis, loading, streaming } = useSessionAnalysis({
		sessionId,
		examName,
		tenantName,
		outputLanguage,
		demoExamId,
		examId,
		appId,
		getAuthHeaders,
		userData,
		goldStandardData,
		ready,
		additionalContext,
	});

	const [showFullReport, setShowFullReport] = useState<boolean | undefined>(
		false,
	);

	useEffect(() => {
		setShowFullReport(shouldShowFullReport);
	}, [shouldShowFullReport]);

	const version = packageJson.version;
	const handleProxyTracking = (
		eventName: string,
		params?: Record<string, string>,
		increment?: boolean,
	) => {
		const additionalParams = {
			context,
			version,
			feature: "AI_SESSION_ANALYSIS",
		};
		handleTracking(
			eventName,
			{
				...(params || {}),
				...additionalParams,
			},
			increment,
		);
	};
	const toggleShowCompleteReport = () => {
		if (!showFullReport) {
			handleProxyTracking(
				events.viewReportClicked,
				{
					sessionId: `${sessionId}`,
				},
				true,
			);
		}
		setShowFullReport((prev) => !prev);
	};
	return (
		<>
			<SessionAnalysisCard
				sessionAnalysis={sessionAnalysis}
				loading={loading}
				toggleShowCompleteReport={toggleShowCompleteReport}
				appFont={appFont}
				title={cardTitle}
				subtitle={cardSubtitle}
				icon={cardIcon}
				contentHeight={cardContentHeight}
				actionLabel={cardActionLabel}
				containerStyle={cardContainerStyle}
				cardMode={cardMode}
			/>
			{showFullReport && (
				<FullReportDialog
					fontSize={fontSize}
					loading={loading}
					visible={showFullReport}
					handleDismiss={toggleShowCompleteReport}
					sessionAnalysisData={sessionAnalysis}
					handleTracking={handleProxyTracking}
					feedbackDialogParams={feedbackDialogParams}
					bottomContent={bottomContent}
					sessionMetadata={sessionMetadata}
					streaming={streaming}
					appFont={appFont}
				/>
			)}
		</>
	);
};
